import React, { useEffect } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { observer } from 'mobx-react';

import { confirm } from '../components/shared/Alert';
import Text from '../components/shared/Text';
import Icon from '../components/shared/Icon';
import Image from '../components/shared/Image';
import Card from '../components/shared/Card';

import { getLocalValue, t, action } from '../utils/appUtils';
import { itemCanBeBought } from '../utils/warriorUtils';
import { getBuilding } from '../utils/buildingUtils';
import { getIsland } from '../utils/utils';

import appStore from '../stores/app';
import heroStore from '../stores/hero';
import buildingData from '../stores/buildingData';

import { buildingAction } from '../lib/apiCalls';
import { BUILDING_MODULES } from '../lib/constants';
import { buildingsImageRequire } from '../lib/imagesRequire';

import { BG_OFFSET, PaperBackground, MAP_DIMENSIONS } from '../components/Island/IslandScreen';
import { WINDOW_WIDTH, pOrT } from '../components/styleConstants';

type Props = {
  id: string;
};

type Destination = {
  island: string;
  price: number;
  coordinateX: number;
  coordinateY: number;
};

const Portal = observer(({ id }: Props) => {
  const building = getBuilding(appStore.initData.buildings, id)!;

  useEffect(() => {
    buildingData.fetch(building.id);
  }, []);

  if (!buildingData.data) return null;

  const destinations: Destination[] = (buildingData.data.data?.destinations as Destination[]) || [];

  return (
    <View>
      <PaperBackground width={MAP_DIMENSIONS.width} height={MAP_DIMENSIONS.height} />
      <View
        style={{
          marginLeft:
            MAP_DIMENSIONS.width + 15 > WINDOW_WIDTH
              ? BG_OFFSET / 2
              : (WINDOW_WIDTH - MAP_DIMENSIONS.width + 15) / 2,
          marginTop: BG_OFFSET / 2,
          width: MAP_DIMENSIONS.width - BG_OFFSET,
          height: MAP_DIMENSIONS.height - BG_OFFSET,
          overflow: 'hidden',
        }}
      >
        <Image
          style={{
            width: MAP_DIMENSIONS.width - BG_OFFSET,
            height: MAP_DIMENSIONS.height - BG_OFFSET,
          }}
          resizeMode="stretch"
          source={buildingsImageRequire(building.data.image)}
        />
      </View>
      <Card
        title={getLocalValue(building)}
        menuItemsLength={destinations.length}
        style={{
          position: 'absolute',
          right: 0,
        }}
      >
        {destinations.map((dest, index) => {
          const island = getIsland(appStore.initData.islands, dest.island);
          const islandName = island ? getLocalValue(island) : dest.island;
          const canBuy = itemCanBeBought(heroStore.hero, { price: dest.price });
          return (
            <View key={dest.island} style={{ flexDirection: 'row' }}>
              <TouchableOpacity
                style={{
                  flexDirection: 'row',
                  marginTop: index ? 4 : 0,
                  opacity: canBuy ? 1 : 0.4,
                }}
                disabled={!canBuy}
                onPress={() =>
                  action(() => {
                    confirm(
                      t('portal.travelFor', { name: islandName, price: dest.price }),
                      async () => {
                        await buildingAction(
                          building.id,
                          (BUILDING_MODULES as any).PORTAL.ACTIONS.TELEPORT,
                          { island: dest.island },
                        );
                      },
                    );
                  })
                }
              >
                <Text bold style={{ fontSize: pOrT(12, 14) }}>
                  {`${islandName} — ${dest.price}`}
                </Text>
                <Icon
                  style={{ marginTop: 1, marginLeft: 4, width: 16, height: 16 }}
                  size={14}
                  name="enter"
                  title={t('enter')}
                  source={require('../assets/images/enter.png')}
                />
              </TouchableOpacity>
            </View>
          );
        })}
      </Card>
    </View>
  );
});

(Portal as any).displayName = 'Portal';

export default Portal;
